// F-09 writes from the web, the same rows as PFStore's BudgetEdits: an expense with its payers,
// splits and rate snapshots (ECB via Frankfurter, ADR-0005), settle-ups and goals.
import { crossRate, split } from '@pf/money';
import { newUuidv7 } from '@pf/sync-rules';
import { realNow, stampNow } from '../clock.ts';
import type { Edit } from '../sync.ts';
import type { Entry } from './trip.ts';

export interface RateSnapshot {
  to: string;
  rate: string;
  date: string;
  source: string;
}

const FRANKFURTER = 'https://api.frankfurter.dev/v1';
const cache = new Map<string, Promise<{ date: string; rates: Record<string, string> } | null>>();

/** EUR → each code for `date` (latest when it's today or later), with the rates kept as sent. */
function eurRates(codes: string[], date: string | null) {
  const symbols = codes
    .filter((c) => c !== 'EUR')
    .sort()
    .join(',');
  const key = `${date ?? 'latest'}/${symbols}`;
  let hit = cache.get(key);
  if (!hit) {
    hit = fetch(`${FRANKFURTER}/${date ?? 'latest'}?base=EUR&symbols=${symbols}`)
      .then(async (res) => {
        if (!res.ok) return null;
        const text = await res.text();
        const day = /"date"\s*:\s*"(\d{4}-\d{2}-\d{2})"/.exec(text)?.[1];
        const block = /"rates"\s*:\s*\{([^}]*)\}/.exec(text)?.[1];
        if (!day || block === undefined) return null;
        const rates: Record<string, string> = { EUR: '1' };
        for (const m of block.matchAll(/"([A-Z]{3})"\s*:\s*([0-9.]+)/g))
          rates[m[1] as string] = m[2] as string;
        return { date: day, rates };
      })
      .catch(() => null);
    cache.set(key, hit);
  }
  return hit;
}

/** Snapshots from `currency` to each target; ones the ECB doesn't cover are left out ("rate pending"). */
export async function snapshots(
  currency: string,
  targets: string[],
  date: string | null,
): Promise<RateSnapshot[]> {
  const wanted = [...new Set(targets)].filter((c) => c !== currency);
  if (wanted.length === 0) return [];
  const today = new Date(realNow()).toISOString().slice(0, 10);
  const got = await eurRates([currency, ...wanted], date && date < today ? date : null);
  if (!got) return [];
  const base = got.rates[currency];
  return wanted.flatMap((to) => {
    const quote = got.rates[to];
    const rate = base && quote ? crossRate(base, quote) : null;
    return rate ? [{ to, rate, date: got.date, source: 'ecb' }] : [];
  });
}

export type SplitChoice =
  | { kind: 'equal'; users: string[] }
  | { kind: 'percent'; byUser: Record<string, string> }
  | { kind: 'exact'; byUser: Record<string, number> };

export function shares(choice: SplitChoice, total: number) {
  if (choice.kind === 'equal') {
    const parts = split(
      total,
      choice.users.map(() => '1'),
    );
    return parts
      ? choice.users.map((userId, i) => ({
          userId,
          method: 'equal',
          value: null,
          amount: parts[i] as number,
        }))
      : null;
  }
  if (choice.kind === 'percent') {
    const users = Object.keys(choice.byUser).sort();
    const parts = split(
      total,
      users.map((u) => choice.byUser[u] as string),
    );
    return parts
      ? users.map((userId, i) => ({
          userId,
          method: 'percent',
          value: choice.byUser[userId] as string,
          amount: parts[i] as number,
        }))
      : null;
  }
  const users = Object.keys(choice.byUser).sort();
  if (users.reduce((a, u) => a + (choice.byUser[u] as number), 0) !== total) return null;
  return users.map((userId) => ({
    userId,
    method: 'exact',
    value: String(choice.byUser[userId]),
    amount: choice.byUser[userId] as number,
  }));
}

export interface ExpenseDraft {
  description: string;
  amountMinor: number;
  currency: string;
  categoryId: string | null;
  dateLocal: string | null;
  timeLocal: string | null;
  tz: string | null;
  destinationId: string | null;
  itemId: string | null;
  status: string;
  dueDate: string | null;
  /** Set = personal, private to this user, outside splits (FR-BUD-19). */
  personalFor: string | null;
  payers: Record<string, number>;
  split: SplitChoice;
}

const fields = (d: ExpenseDraft) => ({
  description: d.description,
  amountMinor: d.amountMinor,
  currency: d.currency,
  categoryId: d.categoryId,
  dateLocal: d.dateLocal,
  timeLocal: d.timeLocal,
  tz: d.tz,
  destinationId: d.destinationId,
  itemId: d.itemId,
  status: d.status,
  dueDate: d.dueDate,
  source: 'manual',
});

const rateEdit = (tripId: string, expenseId: string, owner: string | null, r: RateSnapshot): Edit => ({
  table: 'expenseRate',
  id: newUuidv7(),
  tripId,
  ownerUserId: owner,
  fields: { expenseId, toCurrency: r.to, rate: r.rate, rateDate: r.date, rateSource: r.source },
});

const deleted = (table: string, id: string, tripId: string, owner: string | null): Edit => ({
  table,
  id,
  tripId,
  ownerUserId: owner,
  fields: { deletedAt: stampNow() },
});

function children(
  tripId: string,
  expenseId: string,
  d: ExpenseDraft,
  owner: string | null,
  existing?: Entry,
): Edit[] {
  const edits: Edit[] = [];
  const payers = d.personalFor ? { [d.personalFor]: d.amountMinor } : d.payers;
  for (const user of Object.keys(payers).sort()) {
    const id = existing?.payers.find((p) => p.userId === user)?.id ?? newUuidv7();
    edits.push({
      table: 'expensePayer',
      id,
      tripId,
      ownerUserId: owner,
      fields: { expenseId, userId: user, amountMinor: payers[user] as number },
    });
  }
  for (const p of existing?.payers ?? [])
    if (payers[p.userId] === undefined) edits.push(deleted('expensePayer', p.id, tripId, owner));
  const wanted = d.personalFor ? [] : (shares(d.split, d.amountMinor) ?? []);
  for (const s of wanted) {
    const id = existing?.splits.find((x) => x.userId === s.userId)?.id ?? newUuidv7();
    edits.push({
      table: 'expenseSplit',
      id,
      tripId,
      ownerUserId: owner,
      fields: { expenseId, userId: s.userId, method: s.method, value: s.value, amountMinor: s.amount },
    });
  }
  for (const s of existing?.splits ?? [])
    if (!wanted.some((w) => w.userId === s.userId)) edits.push(deleted('expenseSplit', s.id, tripId, owner));
  return edits;
}

export function deleteExpense(tripId: string, e: Entry): Edit[] {
  return [
    deleted('expense', e.id, tripId, e.ownerUserId),
    ...e.payers.map((p) => deleted('expensePayer', p.id, tripId, e.ownerUserId)),
    ...e.splits.map((s) => deleted('expenseSplit', s.id, tripId, e.ownerUserId)),
  ];
}

/** A new expense, an update, or (when it moves between shared and personal) delete + recreate. */
export function saveExpense(
  tripId: string,
  d: ExpenseDraft,
  rates: RateSnapshot[],
  existing?: Entry,
): Edit[] {
  const ratesFor = (owner: string | null, id: string, have: Record<string, string>) =>
    rates
      .filter((r) => r.to !== d.currency && (existing?.currency !== d.currency || have[r.to] === undefined))
      .map((r) => rateEdit(tripId, id, owner, r));
  if (existing && existing.ownerUserId === d.personalFor) {
    return [
      { table: 'expense', id: existing.id, tripId, ownerUserId: existing.ownerUserId, fields: fields(d) },
      ...children(tripId, existing.id, d, existing.ownerUserId, existing),
      ...ratesFor(existing.ownerUserId, existing.id, existing.rates),
    ];
  }
  const id = newUuidv7();
  return [
    ...(existing ? deleteExpense(tripId, existing) : []),
    { table: 'expense', id, tripId, ownerUserId: d.personalFor, fields: fields(d) },
    ...children(tripId, id, d, d.personalFor),
    ...rates.filter((r) => r.to !== d.currency).map((r) => rateEdit(tripId, id, d.personalFor, r)),
  ];
}

/** A payment between us, with its rate to the other home currency at the balance-implied rate (FR-BUD-16). */
export function settle(
  tripId: string,
  s: {
    from: string;
    to: string;
    amountMinor: number;
    currency: string;
    date: string;
    otherCurrency?: string;
    impliedRate?: string | null;
  },
): Edit[] {
  const id = newUuidv7();
  const edits: Edit[] = [
    {
      table: 'settlement',
      id,
      tripId,
      fields: {
        fromUserId: s.from,
        toUserId: s.to,
        amountMinor: s.amountMinor,
        currency: s.currency,
        dateLocal: s.date,
      },
    },
  ];
  if (s.otherCurrency && s.otherCurrency !== s.currency && s.impliedRate)
    edits.push(
      rateEdit(tripId, id, null, {
        to: s.otherCurrency,
        rate: s.impliedRate,
        date: s.date,
        source: 'implied',
      }),
    );
  return edits;
}

export const deleteSettlement = (tripId: string, id: string) => deleted('settlement', id, tripId, null);

export function addGoal(
  tripId: string,
  g: {
    owner: string | null;
    categoryId: string | null;
    amountMinor: number;
    currency: string;
    period: string;
  },
): Edit {
  return {
    table: 'budget',
    id: newUuidv7(),
    tripId,
    ownerUserId: g.owner,
    fields: { categoryId: g.categoryId, amountMinor: g.amountMinor, currency: g.currency, period: g.period },
  };
}

export const deleteGoal = (tripId: string, id: string, owner: string | null) =>
  deleted('budget', id, tripId, owner);

export const payInstalment = (tripId: string, id: string, owner: string | null, paid: boolean): Edit => ({
  table: 'instalment',
  id,
  tripId,
  ownerUserId: owner,
  fields: { paidAt: paid ? stampNow() : null },
});
